import { type QueryHookOptions } from "@apollo/client";
import { useSpacesQuery } from "#graphql/supergraphHooks";
import { type SpacesQuery, type SpacesQueryVariables } from "#graphql/supergraphTypes";

/** A single Space as returned by the `spaces` query (DV-195). */
export type Space = SpacesQuery["spaces"][number];

export interface UseSpacesReturn {
  spaces: Space[];
  loading: boolean;
  error: ReturnType<typeof useSpacesQuery>["error"];
  refetch: ReturnType<typeof useSpacesQuery>["refetch"];
}

/**
 * DV-195 — list active Spaces the caller can access, optionally filtered by domain.
 *
 * @example
 * const { spaces, loading } = useSpaces({ domain: DomainSlug.fund_admin });
 */
export const useSpaces = (
  variables?: SpacesQueryVariables,
  options?: Omit<QueryHookOptions<SpacesQuery, SpacesQueryVariables>, "variables">,
): UseSpacesReturn => {
  // `no-cache`: these hooks don't yet wire up cache updates (see the mutation
  // TODOs), so reading straight from the network avoids serving a stale list
  // after a create/update. Moving back to the `cache-and-network` default
  // (jsq-react.mdc) once mutation cache-wiring lands is tracked in DV-399.
  // Overridable via `options`.
  const { data, loading, error, refetch } = useSpacesQuery({
    fetchPolicy: "no-cache",
    ...options,
    variables,
  });

  return {
    spaces: data?.spaces ?? [],
    loading,
    error,
    refetch,
  };
};
